/**
 * The walkthrough instruments.
 *
 * These are the pieces /how-it-works is assembled from, plus the small looping
 * animation the landing hero carries. They are diagrams of the product, not
 * instances of it: no repository is read, no API is called, nothing leaves the
 * tab. That is not a limitation to apologise for — it is the same claim the
 * product makes, honoured by the page that makes it.
 *
 * Two labels run through every caption and are used consistently:
 *
 *   measured      a figure from the published benchmark corpus (22 public
 *                 repositories, pinned tags, the shipped build). It also
 *                 appears in entities/metrics.ts with its provenance.
 *   illustrative  a sequence that is real and per-step costs that are round
 *                 numbers chosen for teaching.
 *
 * Nothing here encodes the compact projection, the ranking policy or the graph
 * schema. What a task *costs* is the claim; how the projection is spelled is
 * the product.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'

import {
  formatNumber,
  formatTokens,
  sendPacket,
  sleep,
  useCounter,
  usePrefersReducedMotion,
  useRunner,
} from './motion'

/* ─────────────────────────────────────────────── hero pulse ─────────── */

interface FlowStep {
  path: string
  box: string
  text: ReactNode
}

const FLOW: FlowStep[] = [
  {
    path: 'p1',
    box: 'rt',
    text: (
      <>
        The agent asks in words — <b>“where is webhook signature verification enforced?”</b> — over
        MCP, on localhost.
      </>
    ),
  },
  {
    path: 'p2',
    box: 'graph',
    text: (
      <>
        <b>L1 · graph facts.</b> Names, signatures, callers, blast radius, answered from the local
        graph. No file is opened.
      </>
    ),
  },
  {
    path: 'p3',
    box: 'mem',
    text: (
      <>
        <b>Session memory</b> records what has already been shown, so the next turn does not pay for
        it twice.
      </>
    ),
  },
  {
    path: 'p2',
    box: 'graph',
    text: (
      <>
        <b>L2 · compact projections.</b> Ranked over the graph and cut to a token budget you set.{/* claims-ok(token-budget): the per-query retrieval budget, not a spend budget */}
      </>
    ),
  },
  {
    path: 'p5',
    box: 'files',
    text: (
      <>
        <b>L3 · exact source</b>, only when the task genuinely needs it — with a stated reason and a
        per-session cap.
      </>
    ),
  },
  {
    path: 'p6',
    box: 'patch',
    text: (
      <>
        An edit goes through a <b>hash-verified patch engine</b>: journaled, atomic, and refused
        outright if the file moved under it.
      </>
    ),
  },
  {
    path: 'p7',
    box: 'agent',
    text: (
      <>
        The agent receives a <b>budgeted answer</b> instead of a wall of source — and the graph
        re-indexes only what changed.
      </>
    ),
  },
]

export function FlowMap() {
  const reduced = usePrefersReducedMotion()
  const svgRef = useRef<SVGSVGElement>(null)
  const start = useRunner()
  const [busy, setBusy] = useState(false)
  const [caption, setCaption] = useState<ReactNode>(
    'The agent asks a question in words. Press play to follow it.',
  )

  async function play() {
    const run = start()
    setBusy(true)
    const root = svgRef.current
    if (!root) return
    root.querySelectorAll('.fm-box, .fm-link').forEach((node) => node.classList.remove('is-hot'))

    for (const step of FLOW) {
      if (!run.live) return
      const path = root.querySelector<SVGPathElement>(`#wt-${step.path}`)
      setCaption(step.text)
      path?.classList.add('is-hot')
      root.querySelectorAll(`#wt-${step.box} rect`).forEach((rect) => rect.classList.add('is-hot'))
      await sendPacket(root, path, { ms: 760, count: 2, gap: 150, reduced })
      await sleep(reduced ? 30 : 240)
      path?.classList.remove('is-hot')
    }
    if (!run.live) return
    setCaption(
      <>
        The whole path stayed inside the dashed boundary. <b>Nothing was uploaded.</b>
      </>,
    )
    setBusy(false)
  }

  return (
    <div className="panel">
      <div className="row row--between" style={{ marginBottom: 'var(--s4)', gap: 'var(--s4)' }}>
        <div>
          <h3 style={{ fontFamily: 'var(--display)', fontSize: '1.125rem' }}>One question, end to end</h3>
          <p className="dim" style={{ margin: 'var(--s2) 0 0', fontSize: '0.875rem', maxWidth: '62ch' }}>
            Illustrative animation of the real call path. The dashed boundary is your machine.
          </p>
        </div>
        <button type="button" className="btn btn--sm" onClick={() => void play()} disabled={busy}>
          ▶ Play
        </button>
      </div>

      <div className="wt-map">
        <svg
          ref={svgRef}
          viewBox="0 0 940 250"
          role="img"
          aria-label="A coding agent calls the local Archis runtime, which answers from a local semantic graph, session memory and a hash-verified patch engine over the repository's files."
        >
          <rect className="fm-zone" x="6" y="8" width="928" height="234" rx="4" />
          <text className="fm-z" x="24" y="34">your machine · nothing leaves it</text>

          <g id="wt-agent">
            <rect className="fm-box" x="28" y="76" width="176" height="86" rx="3" />
            <text className="fm-t" x="48" y="112">Coding agent</text>
            <text className="fm-s" x="48" y="132">Claude Code, Codex,</text>
            <text className="fm-s" x="48" y="147">Gemini, your own</text>
          </g>
          <g id="wt-rt">
            <rect className="fm-box" x="290" y="60" width="188" height="118" rx="3" />
            <text className="fm-t" x="310" y="96">Archis runtime</text>
            <text className="fm-s" x="310" y="116">local MCP server</text>
            <text className="fm-s" x="310" y="131">tool router + policy</text>
            <text className="fm-s" x="310" y="146">token ledger per session</text>
          </g>
          <g id="wt-graph">
            <rect className="fm-box" x="566" y="26" width="172" height="72" rx="3" />
            <text className="fm-t" x="586" y="58">Semantic graph</text>
            <text className="fm-s" x="586" y="78">symbols · calls · impact</text>
          </g>
          <g id="wt-mem">
            <rect className="fm-box" x="566" y="112" width="172" height="60" rx="3" />
            <text className="fm-t" x="586" y="140">Session memory</text>
            <text className="fm-s" x="586" y="158">what was already shown</text>
          </g>
          <g id="wt-patch">
            <rect className="fm-box" x="566" y="186" width="172" height="46" rx="3" />
            <text className="fm-t" x="586" y="214">Patch engine</text>
          </g>
          <g id="wt-files">
            <rect className="fm-box" x="800" y="96" width="112" height="90" rx="3" />
            <text className="fm-t" x="820" y="132">Your files</text>
            <text className="fm-s" x="820" y="152">read on</text>
            <text className="fm-s" x="820" y="167">demand only</text>
          </g>

          <path id="wt-p1" className="fm-link" d="M204 119 H290" />
          <path id="wt-p2" className="fm-link" d="M478 100 C520 100 528 62 566 62" />
          <path id="wt-p3" className="fm-link" d="M478 130 C520 130 528 142 566 142" />
          <path id="wt-p4" className="fm-link" d="M478 152 C520 152 528 209 566 209" />
          <path id="wt-p5" className="fm-link" d="M738 62 C776 62 776 120 800 120" />
          <path id="wt-p6" className="fm-link" d="M738 209 C776 209 776 165 800 165" />
          <path id="wt-p7" className="fm-link" d="M290 158 C250 158 240 140 204 140" />

          <text className="fm-tag" x="216" y="112">ask</text>
          <text className="fm-tag" x="212" y="176">budgeted answer</text>
        </svg>
      </div>

      <p className="wt-caption">{caption}</p>
    </div>
  )
}

/* ─────────────────────────────────────────────── pipeline ───────────── */

interface CorpusRow {
  repo: string
  lang: string
  files: number
  src: number
  out: number
  reduction: number
  coverage: number
}

/**
 * Absolute token counts, from the official corpus, re-measured on Archis 0.13.0.
 *
 * `files` is `compression.files_measured` — the files that produced these token
 * counts — not the wider walk total, so the three numbers in a row always describe
 * the same measurement. The pooled row carries no per-file or per-coverage figure
 * because pooling those would invent a number the benchmark does not report.
 */
const CORPUS: CorpusRow[] = [
  { repo: 'gin', lang: 'Go', files: 105, src: 159272, out: 39145, reduction: 75.4, coverage: 83 },
  { repo: 'caddy', lang: 'Go', files: 288, src: 616877, out: 107549, reduction: 82.6, coverage: 92 },
  { repo: 'prometheus', lang: 'Go', files: 703, src: 2521184, out: 497082, reduction: 80.3, coverage: 94 },
  { repo: 'architecture-samples', lang: 'Kotlin', files: 75, src: 55030, out: 12406, reduction: 77.5, coverage: 79 },
  { repo: 'detekt', lang: 'Kotlin', files: 1195, src: 965016, out: 399149, reduction: 58.6, coverage: 95 },
  { repo: 'kotlinx.coroutines', lang: 'Kotlin', files: 1121, src: 905853, out: 306861, reduction: 66.1, coverage: 96 },
  { repo: 'the whole 22-repo corpus (pooled)', lang: '8 languages', files: 0, src: 52119711, out: 14171466, reduction: 72.8, coverage: 0 },
]

const STAGES = [
  { n: '01', name: 'detect', what: 'language + support tier per file' },
  { n: '02', name: 'extract', what: 'parse with tree-sitter' },
  { n: '03', name: 'normalize', what: 'canonical IR' },
  { n: '04', name: 'render', what: 'compact projection' },
  { n: '05', name: 'measure', what: 'tokens + coverage' },
]

function stageCaption(index: number, row: CorpusRow): ReactNode {
  switch (index) {
    case 0:
      return (
        <>
          <b>detect</b> — classifying {row.files ? `${formatNumber(row.files)} files` : 'the corpus'}.
          Every language gets an honest support tier, and anything unsupported degrades rather than
          failing.
        </>
      )
    case 1:
      return (
        <>
          <b>extract</b> — one data-driven tree-sitter extractor over 29 grammars, resolving symbols,
          signatures and relations across files.
        </>
      )
    case 2:
      return (
        <>
          <b>normalize</b> — everything lands in one frozen, language-neutral intermediate
          representation. That contract is what the rest of the system is built on.
        </>
      )
    case 3:
      return (
        <>
          <b>render</b> — a deterministic, byte-identical compact projection. One-way by design: the
          original file is always kept, so this form is free to drop what costs tokens.
        </>
      )
    default:
      return (
        <>
          <b>measure</b> — {formatTokens(row.src)} source tokens down to {formatTokens(row.out)}
          {row.coverage ? `, at ${row.coverage}% declaration coverage` : ', across the pooled corpus'}.
        </>
      )
  }
}

export function CompilePipeline() {
  const reduced = usePrefersReducedMotion()
  const start = useRunner()
  const [pick, setPick] = useState(1)
  const [stage, setStage] = useState(-1)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [caption, setCaption] = useState<ReactNode>('Choose a repository and press compile.')

  const row = CORPUS[pick]!
  const shownSrc = useCounter(stage >= 1 || done ? row.src : 0, reduced)
  const shownOut = useCounter(done ? row.out : stage >= 1 ? row.src : 0, reduced)
  const shownReduction = useCounter(done ? row.reduction : 0, reduced)

  async function run(next = pick) {
    const token = start()
    setBusy(true)
    setDone(false)
    setStage(-1)
    const target = CORPUS[next]!

    for (let i = 0; i < STAGES.length; i += 1) {
      if (!token.live) return
      setStage(i)
      setCaption(stageCaption(i, target))
      await sleep(reduced ? 40 : 700)
    }
    if (!token.live) return
    setStage(STAGES.length)
    setDone(true)
    setCaption(
      <>
        Done. <b>
          {formatTokens(target.src)} → {formatTokens(target.out)} tokens
        </b>{' '}
        — {target.reduction.toFixed(1)}% smaller
        {target.coverage
          ? `, with ${target.coverage}% of declarations still captured as symbols`
          : ' across the pooled corpus'}
        . The compiler is one-way: the source file on disk is untouched.
      </>,
    )
    setBusy(false)
  }

  return (
    <div className="panel">
      <div className="row row--between" style={{ marginBottom: 'var(--s4)', gap: 'var(--s4)' }}>
        <div>
          <h3 style={{ fontFamily: 'var(--display)', fontSize: '1.125rem' }}>chi measure &lt;repo&gt;</h3>
          <p className="dim" style={{ margin: 'var(--s2) 0 0', fontSize: '0.875rem', maxWidth: '62ch' }}>
            Stage names and token totals are real: the corpus repositories are public and pinned to
            released tags, so every figure is reproducible on your own checkout.
          </p>
        </div>
        <div className="row" style={{ gap: 'var(--s3)' }}>
          <label className="sr-only" htmlFor="wt-repo">
            Repository
          </label>
          <select
            id="wt-repo"
            className="select"
            value={pick}
            onChange={(event) => {
              const next = Number(event.target.value)
              setPick(next)
              void run(next)
            }}
          >
            {CORPUS.map((entry, index) => (
              <option key={entry.repo} value={index}>
                {entry.repo} · {entry.lang}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn--sm" onClick={() => void run()} disabled={busy}>
            ▶ Compile
          </button>
        </div>
      </div>

      <ol className="wt-pipe">
        {STAGES.map((entry, index) => (
          <li
            key={entry.name}
            className={`wt-pipe__stage ${stage === index ? 'is-on' : ''} ${stage > index ? 'is-done' : ''}`}
          >
            <span className="wt-pipe__n">{entry.n}</span>
            <span className="wt-pipe__name">{entry.name}</span>
            <span className="wt-pipe__what">{entry.what}</span>
          </li>
        ))}
      </ol>

      <p className="wt-caption">{caption}</p>

      <div className="wt-meters">
        <div className="wt-meter">
          <div className="wt-meter__top">
            <span>Source, as the model would read it</span>
            <b>{stage >= 1 || done ? `${formatTokens(shownSrc)} tok` : '—'}</b>
          </div>
          <div className="wt-meter__track">
            <div className="wt-meter__fill" style={{ width: stage >= 1 || done ? '100%' : '0%' }} />
          </div>
        </div>
        <div className="wt-meter wt-meter--signal">
          <div className="wt-meter__top">
            <span>Compact semantic projection</span>
            <b>{done ? `${formatTokens(shownOut)} tok` : '—'}</b>
          </div>
          <div className="wt-meter__track">
            <div
              className="wt-meter__fill"
              style={{ width: done ? `${(row.out / row.src) * 100}%` : '0%' }}
            />
          </div>
        </div>
      </div>

      <div className="readout-grid" style={{ marginTop: 'var(--s5)' }}>
        <div className="readout">
          <div className="readout__value readout__value--signal">
            {done ? `−${shownReduction.toFixed(1)}%` : '—'}
          </div>
          <div className="readout__caption">token reduction · measured</div>
        </div>
        <div className="readout">
          <div className="readout__value">{done ? (row.coverage ? `${row.coverage}%` : '21–99%') : '—'}</div>
          <div className="readout__caption">declaration coverage</div>
        </div>
        <div className="readout">
          <div className="readout__value">{done ? (row.files ? formatNumber(row.files) : '22') : '—'}</div>
          <div className="readout__caption">{row.files ? 'files parsed' : 'repositories'}</div>
        </div>
      </div>

      <p className="mono-note" style={{ marginTop: 'var(--s5)' }}>
        Coverage is quoted beside every reduction on purpose. A compiler that “compresses” by
        silently dropping code would post a spectacular number and be useless; pairing the two makes
        that impossible to hide. The projection itself is not shown here — the cost of it is the
        claim, and <code>chi measure ./src</code> reproduces that cost on your own code.
      </p>
    </div>
  )
}

/* ─────────────────────────────────────────────── context ladder ─────── */

export type LadderRow = [level: 'L1' | 'L2' | 'L3', what: string, cost: number | 'budget', note?: string]

/** One Archis call as the agent sees it: level, tool, argument, what came back, cost. */
export type ToolCall = [level: 'L1' | 'L2' | 'L3', tool: string, arg: string, result: string, cost: number | 'budget']

export interface Task {
  label: string
  /** Two or three words for a tab; the label is the question itself. */
  short: string
  /** The line both agents end on. Same answer, different cost. */
  answer: string
  base: Array<[string, number]>
  chi: LadderRow[]
  /** The same rows as `chi`, spelled as `chi/<tool>` calls for the hero's terminals. */
  calls: ToolCall[]
}

/** The L2 budget the ladder opens at, and the one the hero's read is drawn with. */
export const DEFAULT_BUDGET = 1600

/* Illustrative traces: the shape is the real call pattern, the costs are round
   numbers in the right order of magnitude rather than one recorded run. The first
   task is also the hero's transcript (`TwoTerminals`), so the two cannot disagree. */
export const TASKS: Task[] = [
  {
    label: 'Where is webhook signature verification enforced?',
    short: 'Find where a check lives',
    answer: 'verifySignature in webhooks/handler.go, enforced from api/middleware.go',
    base: [
      ['list the repository tree', 1200],
      ['read api/router.go', 14800],
      ['read api/middleware.go', 11400],
      ['read webhooks/handler.go', 18900],
      ['read webhooks/handler_test.go', 9600],
      ['read config/security.go', 6100],
    ],
    chi: [
      ['L1', 'search by meaning → 3 symbols in 2 files', 420],
      ['L1', 'callers of the matching symbol', 310],
      ['L2', 'compact projections, ranked to the budget', 'budget'],
      ['L3', 'exact source of one function — reason stated', 2400, 'the only file actually opened'],
    ],
    calls: [
      ['L1', 'search', '"webhook signature verification"', '3 symbols in 2 files', 420],
      ['L1', 'get_relations', 'verifySignature', '1 caller: api/middleware.go', 310],
      ['L2', 'get_symbols', 'ranked to the budget', 'compact projections', 'budget'],
      ['L3', 'get_files', 'webhooks/handler.go verifySignature view=full', 'the only file opened', 2400],
    ],
  },
  {
    label: 'A test started failing after my change — fix it',
    short: 'Fix a failing test',
    answer: 'patched parse_options; the test asserted the old default. 1 file changed, suite green',
    base: [
      ['read the failing test file', 8200],
      ['read the subject under test', 16500],
      ['read two dependencies it calls', 21300],
      ['paste the raw test output', 12400],
      ['re-read the subject after editing', 16500],
    ],
    chi: [
      ['L1', 'locate the failing symbol and its tests', 480],
      ['L1', 'blast radius of the change', 390],
      ['L2', 'compact projections of the involved symbols', 'budget'],
      ['L3', 'exact source of the function to patch', 3100, 'the edit needs the real bytes'],
      ['L1', 'test output, compressed reversibly', 1050, 'every failure line kept; the original stays retrievable'],
    ],
    calls: [
      ['L1', 'search', '"TestParseOptions_default"', 'the symbol and its 2 tests', 480],
      ['L1', 'get_relations', 'parse_options depth=2', 'blast radius: 4 symbols', 390],
      ['L2', 'get_symbols', 'ranked to the budget', 'compact projections', 'budget'],
      ['L3', 'get_files', 'options.go parse_options view=full', 'the edit needs the real bytes', 3100],
      ['L1', 'run', 'tests, compressed reversibly', 'every failure line kept', 1050],
    ],
  },
  {
    label: 'What breaks if I change this function signature?',
    short: 'Change a signature',
    answer: '3 callers in 2 files and 1 implementer break; listed with line numbers',
    base: [
      ['grep for the name', 2100],
      ['read nine files containing call sites', 58700],
      ['read the interface definitions', 12200],
    ],
    chi: [
      ['L1', 'impact query, depth 3', 620],
      ['L1', 'callers, callees and implementers', 430],
      ['L2', 'compact projections of the affected symbols', 'budget'],
    ],
    calls: [
      ['L1', 'get_relations', 'handleRequest depth=3', 'impact: 3 callers, 1 implementer', 620],
      ['L1', 'get_relations', 'callers, callees, implementers', '2 files', 430],
      ['L2', 'get_symbols', 'ranked to the budget', 'compact projections', 'budget'],
    ],
  },
]

/**
 * The ladder. On the landing it opens on a task other than the hero's, with the
 * tasks as visible tabs, so section two asks "does this apply to my work?" rather
 * than drawing the hero's still again as a table. Elsewhere the defaults hold.
 */
export function ContextLadder({
  initialTask = 0,
  picker = 'select',
  intro = true,
}: {
  initialTask?: number
  picker?: 'select' | 'tabs'
  intro?: boolean
} = {}) {
  const reduced = usePrefersReducedMotion()
  const start = useRunner()
  const [taskIndex, setTaskIndex] = useState(initialTask)
  const [budget, setBudget] = useState(DEFAULT_BUDGET)
  const [shown, setShown] = useState(0)

  const task = TASKS[taskIndex]!
  const priced = (cost: number | 'budget') => (cost === 'budget' ? budget : cost)

  const baseRows = task.base.slice(0, shown)
  const chiRows = task.chi.slice(0, shown)
  const baseRunning = baseRows.reduce((total, [, cost]) => total + cost, 0)
  const chiRunning = chiRows.reduce((total, [, , cost]) => total + priced(cost), 0)

  const baseTotal = task.base.reduce((total, [, cost]) => total + cost, 0)
  const chiTotal = task.chi.reduce((total, [, , cost]) => total + priced(cost), 0)
  const l3 = task.chi
    .filter(([level]) => level === 'L3')
    .reduce((total, [, , cost]) => total + priced(cost), 0)
  const complete = shown >= Math.max(task.base.length, task.chi.length)

  useEffect(() => {
    const token = start()
    setShown(0)
    const rows = Math.max(task.base.length, task.chi.length)
    const play = async () => {
      for (let i = 1; i <= rows; i += 1) {
        await sleep(reduced ? 20 : 320)
        if (!token.live) return
        setShown(i)
      }
    }
    void play()
  }, [taskIndex, budget, reduced, start, task.base.length, task.chi.length])

  const reduction = (1 - chiTotal / baseTotal) * 100

  return (
    <div className="panel">
      <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
        {intro && (
          <div>
            <h3 style={{ fontFamily: 'var(--display)', fontSize: '1.125rem' }}>Same task, two agents</h3>
            <p className="dim" style={{ margin: 'var(--s2) 0 0', fontSize: '0.875rem', maxWidth: '64ch' }}>
              Left: an agent reading files. Right: the same agent asking through Archis under a budget you
              set. Illustrative step costs; the measured corpus result is quoted underneath.
            </p>
          </div>
        )}

        {picker === 'tabs' && (
          <div className="wt-tabs" role="tablist" aria-label="The task">
            {TASKS.map((entry, index) => (
              <button
                key={entry.label}
                type="button"
                role="tab"
                aria-selected={index === taskIndex}
                className="wt-tab"
                onClick={() => setTaskIndex(index)}
              >
                {entry.short}
              </button>
            ))}
          </div>
        )}

        {picker === 'tabs' && (
          <p className="wt-question">
            <span className="wt-question__caret">›</span> {task.label}
          </p>
        )}

        <div className="row" style={{ gap: 'var(--s5)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {picker === 'select' && (
            <div className="field" style={{ margin: 0, minWidth: 0, flex: '1 1 18rem', maxWidth: '100%' }}>
              <label className="field__label" htmlFor="wt-task">
                The task
              </label>
              <select
                id="wt-task"
                className="select"
                value={taskIndex}
                onChange={(event) => setTaskIndex(Number(event.target.value))}
              >
                {TASKS.map((entry, index) => (
                  <option key={entry.label} value={index}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field" style={{ margin: 0, minWidth: 0, flex: '1 1 14rem', maxWidth: '100%' }}>
            <label className="field__label" htmlFor="wt-budget">
              L2 budget per selection
            </label>
            <div className="row" style={{ gap: 'var(--s3)', alignItems: 'center' }}>
              <input
                id="wt-budget"
                className="calc__slider"
                type="range"
                min={400}
                max={3200}
                step={200}
                value={budget}
                onChange={(event) => setBudget(Number(event.target.value))}
                style={{ flex: 1, minWidth: 0, ['--fill' as string]: `${((budget - 400) / (3200 - 400)) * 100}%` }}
              />
              <output className="mono-note" style={{ minWidth: '7ch', flexShrink: 0 }}>
                {formatNumber(budget)} tok
              </output>
            </div>
          </div>
        </div>

        <div className="grid-2" style={{ gap: 'var(--s4)' }}>
          <div className="wt-panel wt-panel--base">
            <div className="wt-panel__head">
              <span>Reading files directly</span>
              <span className="wt-panel__total">{formatTokens(baseRunning)} tok</span>
            </div>
            <ol className="wt-trace">
              {baseRows.map(([what, cost]) => (
                <li key={what}>
                  <span className="wt-trace__lvl">read</span>
                  <span>{what}</span>
                  <span className="wt-trace__cost">{formatNumber(cost)}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="wt-panel wt-panel--signal">
            <div className="wt-panel__head">
              <span>Through Archis</span>
              <span className="wt-panel__total">{formatTokens(chiRunning)} tok</span>
            </div>
            <ol className="wt-trace">
              {chiRows.map(([level, what, cost, note]) => (
                <li key={what}>
                  <span className={`wt-trace__lvl wt-trace__lvl--${level.toLowerCase()}`}>{level}</span>
                  <span>
                    {what}
                    {note ? <span className="wt-trace__note">{note}</span> : null}
                  </span>
                  <span className="wt-trace__cost">{formatNumber(priced(cost))}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* One line of result, and only once the run is complete: a figure that changes
            while the columns are still filling would be a number that was never true. */}
        <p className="wt-result" aria-live="polite">
          {complete ? (
            <>
              <b className="signal">−{reduction.toFixed(1)}%</b> less context on this task
              <span className="wt-result__sep"> · </span>
              <b>{(baseTotal / chiTotal).toFixed(1)}×</b> fewer tokens
              <span className="wt-result__sep"> · </span>
              {l3 ? <><b>{Math.round((l3 / chiTotal) * 100)}%</b> of it exact source</> : 'no source opened'}
            </>
          ) : (
            <span className="dim">Running…</span>
          )}
        </p>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────── privacy boundary ───── */

export function PrivacyBoundary() {
  const reduced = usePrefersReducedMotion()
  const svgRef = useRef<SVGSVGElement>(null)
  const start = useRunner()
  const [busy, setBusy] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const [caption, setCaption] = useState<ReactNode>(
    'Press play to trace what actually crosses the boundary.',
  )

  async function play() {
    const run = start()
    setBusy(true)
    setBlocked(false)
    const root = svgRef.current
    if (!root) return
    root.querySelectorAll('.fm-box, .fm-link').forEach((node) =>
      node.classList.remove('is-hot', 'is-blocked'),
    )

    const hot = (id: string, cls = 'is-hot') =>
      root.querySelectorAll(`#wt-${id} rect`).forEach((rect) => rect.classList.add(cls))

    setCaption(
      <>
        You sign in. An identity token proves <b>who you are</b> — and nothing about what is on your
        disk.
      </>,
    )
    hot('pid')
    const p1 = root.querySelector<SVGPathElement>('#wt-b1')
    p1?.classList.add('is-hot')
    await sendPacket(root, p1, { ms: 760, count: 2, gap: 140, reduced })
    p1?.classList.remove('is-hot')
    if (!run.live) return

    setCaption(
      <>
        The subscription decides what you are entitled to, and the server issues a{' '}
        <b>signed licence</b> that says so. The CLI verifies it <b>offline, on every protected
        command</b> — no network call sits in the path of your work.
      </>,
    )
    hot('pbill')
    hot('please')
    const p2 = root.querySelector<SVGPathElement>('#wt-b2')
    p2?.classList.add('is-hot')
    await sendPacket(root, p2, { ms: 820, count: 2, gap: 140, reduced })
    p2?.classList.remove('is-hot')
    hot('pcli')
    await sleep(reduced ? 40 : 800)
    if (!run.live) return

    setCaption(
      <>
        Now the interesting one. Suppose something tried to send your <b>source</b> the other way.
      </>,
    )
    hot('psrc')
    hot('pidx')
    await sleep(reduced ? 40 : 600)
    const blockedPath = root.querySelector<SVGPathElement>('#wt-b3')
    await sendPacket(root, blockedPath, { ms: 700, className: 'pkt pkt--coral', radius: 5, reduced })
    if (!run.live) return
    setBlocked(true)
    hot('psrc', 'is-blocked')
    hot('pidx', 'is-blocked')
    setCaption(
      <>
        It has nowhere to arrive. <b>There is no endpoint on our side that accepts source code</b> —
        the guarantee is enforced by absence, not by a promise in a policy document.
      </>,
    )
    await sleep(reduced ? 40 : 1400)
    if (!run.live) return

    setCaption(
      <>
        The only thing that ever travels upward is <b>opt-in product telemetry</b>: off by default,
        coarse buckets, and structurally incapable of carrying a path, a symbol, a query or an error
        message.
      </>,
    )
    hot('ptel')
    const p4 = root.querySelector<SVGPathElement>('#wt-b4')
    p4?.classList.add('is-hot')
    await sendPacket(root, p4, { ms: 900, reduced })
    p4?.classList.remove('is-hot')
    setBusy(false)
  }

  return (
    <div className="panel">
      <div className="row row--between" style={{ marginBottom: 'var(--s4)', gap: 'var(--s4)' }}>
        <div>
          <h3 style={{ fontFamily: 'var(--display)', fontSize: '1.125rem' }}>What crosses, and what cannot</h3>
          <p className="dim" style={{ margin: 'var(--s2) 0 0', fontSize: '0.875rem', maxWidth: '62ch' }}>
            Everything semantic happens on your machine; everything commercial happens in the cloud.
            The third packet is the one worth watching.
          </p>
        </div>
        <button type="button" className="btn btn--sm" onClick={() => void play()} disabled={busy}>
          ▶ Play
        </button>
      </div>

      <div className="wt-map">
        <svg
          ref={svgRef}
          viewBox="0 0 940 300"
          role="img"
          aria-label="The local machine holds source, index, graph and runtime; our servers hold identity, subscription and licence issuing. A signed licence descends and is verified offline; opt-in coarse telemetry ascends; an attempt to send source code has nowhere to arrive."
        >
          <rect className="fm-zone" x="6" y="8" width="452" height="284" rx="4" />
          <text className="fm-z" x="26" y="34">your machine</text>
          <rect className="fm-zone" x="482" y="8" width="452" height="284" rx="4" />
          <text className="fm-z" x="502" y="34">archis.dev</text>

          <g id="wt-psrc">
            <rect className="fm-box" x="30" y="58" width="180" height="72" rx="3" />
            <text className="fm-t" x="50" y="90">Source &amp; history</text>
            <text className="fm-s" x="50" y="110">never uploaded</text>
          </g>
          <g id="wt-pidx">
            <rect className="fm-box" x="30" y="150" width="180" height="80" rx="3" />
            <text className="fm-t" x="50" y="182">Index &amp; graph</text>
            <text className="fm-s" x="50" y="202">on your disk,</text>
            <text className="fm-s" x="50" y="217">under your control</text>
          </g>
          <g id="wt-pcli">
            <rect className="fm-box" x="250" y="104" width="176" height="92" rx="3" />
            <text className="fm-t" x="270" y="138">Archis CLI + runtime</text>
            <text className="fm-s" x="270" y="158">verifies the licence</text>
            <text className="fm-s" x="270" y="173">offline, every command</text>
          </g>

          <g id="wt-pid">
            <rect className="fm-box" x="508" y="52" width="196" height="66" rx="3" />
            <text className="fm-t" x="528" y="82">Identity &amp; team</text>
            <text className="fm-s" x="528" y="102">sign-in, seats, invites</text>
          </g>
          <g id="wt-pbill">
            <rect className="fm-box" x="508" y="132" width="196" height="66" rx="3" />
            <text className="fm-t" x="528" y="162">Subscription</text>
            <text className="fm-s" x="528" y="182">plan, seats, billing</text>
          </g>
          <g id="wt-please">
            <rect className="fm-box" x="508" y="212" width="196" height="66" rx="3" />
            <text className="fm-t" x="528" y="242">Signed licence</text>
            <text className="fm-s" x="528" y="262">what the product may do</text>
          </g>
          <g id="wt-ptel">
            <rect className="fm-box" x="750" y="132" width="160" height="80" rx="3" />
            <text className="fm-t" x="770" y="164">Telemetry</text>
            <text className="fm-s" x="770" y="184">opt-in, off by</text>
            <text className="fm-s" x="770" y="199">default, no paths</text>
          </g>

          <path id="wt-b1" className="fm-link" d="M426 130 C466 130 470 85 508 85" />
          <path id="wt-b2" className="fm-link" d="M508 245 C466 245 466 170 426 170" />
          <path id="wt-b3" className="fm-link" d="M214 190 H452" fill="none" stroke="none" />
          {/* Telemetry is routed under the cloud column rather than straight across
              it: the direct line passed through the subscription box and read as a
              strikethrough on its label. */}
          <path id="wt-b4" className="fm-link" d="M300 196 V284 H830 V212" />

          <text className="fm-tag" x="436" y="112">sign in</text>
          <text className="fm-tag" x="436" y="212">licence, signed</text>

          {blocked ? (
            <g>
              <circle cx="462" cy="190" r="13" className="wt-block__ring" />
              <path d="M456 184 L468 196 M468 184 L456 196" className="wt-block__x" />
              {/* Low inside the machine's own zone, and kept short: the gap between
                  the two zones is 24px wide, so a caption placed there collided with
                  the subscription box, and a longer one here runs into the telemetry
                  route. The full sentence is in the caption under the diagram. */}
              <text className="fm-tag wt-block__label" x="30" y="262">
                ✕ no endpoint accepts source
              </text>
            </g>
          ) : null}
        </svg>
      </div>

      <p className="wt-caption">{caption}</p>
    </div>
  )
}
